import { Building2, Edit2, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { apiUrl } from '@/config/api';
import { authGet, authPost, authPut } from '@/lib/authFetch';

const API_BASE = apiUrl('/api/vendors');

type VendorStatus = 'active' | 'suspended';

interface Vendor {
  id: string;
  name: string;
  code: string;
  status: VendorStatus;
  createdAt: string;
}

interface VendorFormData {
  name: string;
  code: string;
  status: VendorStatus;
}

// Sort helper
const SortIndicator = ({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) => {
  if (!active) return null;
  return <span className="ml-1">{direction === 'asc' ? '▲' : '▼'}</span>;
};

// Pagination sizes
const PAGE_SIZES = [10, 20, 50, 100];

export default function VendorList() {
  const { t } = useTranslation();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

  // Sorting
  const [sortKey, setSortKey] = useState<'name' | 'code' | 'createdAt'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Form state
  const [formData, setFormData] = useState<VendorFormData>({
    name: '',
    code: '',
    status: 'active',
  });

  const fetchVendors = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await authGet(API_BASE);
      if (response.ok) {
        const data = (await response.json()) as Vendor[];
        setVendors(data);
      } else {
        setError(t('common.errors.fetchFailed'));
      }
    } catch (error_) {
      console.error(error_);
      setError(t('common.errors.networkError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // Derived State: Filtering & Sorting
  const sortedVendors = [...vendors].sort((a, b) => {
    const valA = (a[sortKey] || '').toLowerCase();
    const valB = (b[sortKey] || '').toLowerCase();
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination Logic
  const totalItems = sortedVendors.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedVendors = sortedVendors.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Handlers
  const handleSort = (key: 'name' | 'code' | 'createdAt') => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingVendor) {
        await authPut(`${API_BASE}/${editingVendor.id}`, formData);
      } else {
        await authPost(API_BASE, formData);
      }
      setIsModalOpen(false);
      setEditingVendor(null);
      setFormData({ name: '', code: '', status: 'active' });
      fetchVendors();
    } catch (error_) {
      console.error(error_);
      setError(t('common.errors.saveFailed'));
    }
  };

  const openEditModal = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setFormData({ name: vendor.name, code: vendor.code, status: vendor.status });
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingVendor(null);
    setFormData({ name: '', code: '', status: 'active' });
    setIsModalOpen(true);
  };

  const renderTableContent = () => {
    if (isLoading) {
      return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;
    }
    if (error) {
      return <div className="p-8 text-center text-red-500">{error}</div>;
    }
    if (vendors.length === 0) {
      return <div className="p-8 text-center text-gray-500">{t('vendors.messages.noData')}</div>;
    }

    return (
      <>
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-500 uppercase font-medium border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">
                  {t('vendors.fields.name')}
                  <SortIndicator active={sortKey === 'name'} direction={sortDir} />
                </div>
              </th>
              <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('code')}>
                <div className="flex items-center gap-1">
                  {t('vendors.fields.code')}
                  <SortIndicator active={sortKey === 'code'} direction={sortDir} />
                </div>
              </th>
              <th className="px-6 py-4">{t('vendors.fields.status')}</th>
              <th className="px-6 py-4 text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginatedVendors.map((vendor) => (
              <tr key={vendor.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 font-medium text-gray-900">{vendor.name}</td>
                <td className="px-6 py-4 font-mono text-gray-600">{vendor.code}</td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      vendor.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {t(`vendors.status.${vendor.status}`)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    type="button"
                    onClick={() => openEditModal(vendor)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors text-xs"
                    title={t('vendors.edit')}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Control */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">{t('common.pagination.total', { count: totalItems })}</div>
          <div className="flex items-center gap-4">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {t('common.pagination.perPage', { count: size })}
                </option>
              ))}
            </select>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </Button>
              <span className="px-3 py-1 flex items-center text-sm">
                {currentPage} / {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
            <Building2 className="w-7 h-7 text-gray-700" />
            {t('vendors.title')}
          </h1>
          <p className="text-gray-500 mt-1">{t('vendors.description')}</p>
        </div>
        <Button onClick={openCreateModal} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {t('vendors.create')}
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">{renderTableContent()}</div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingVendor ? t('vendors.modal.editTitle') : t('vendors.modal.createTitle')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="vendor-name" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('vendors.fields.name')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="vendor-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="vendor-code" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('vendors.fields.code')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="vendor-code"
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="vendor-status" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('vendors.fields.status')}
                </label>
                <select
                  id="vendor-status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as VendorStatus })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="active">{t('vendors.status.active')}</option>
                  <option value="suspended">{t('vendors.status.suspended')}</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit">{editingVendor ? t('common.save') : t('common.create')}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
